import type { Label, MessageSummary } from '@/api/types';

/**
 * Keywords to labels (plan/17 §2, §5).
 *
 * The server hands every message its label keywords as it spelled them, and
 * the account's labels as rows. Everything the UI shows as a chip goes through
 * `labelsForKeywords`, which is the one place that decides what a keyword
 * MEANS — so a list row, the reading pane and the picker cannot disagree.
 *
 * - Keywords compare **ASCII case-insensitively**, as the mail server compares
 *   them: `$label_work` on a message is the label whose keyword is
 *   `$label_Work` (the server keeps the first STORE's spelling).
 * - A label's `previousKeyword` counts too: while a rename is rewriting, and
 *   after one that a folder refused, the old keyword is still this label.
 * - A label in state `deleting` is not shown, and neither is a keyword no
 *   label claims (another client's keyword that happens to share the prefix).
 */

/** `labels.IsLabelKeyword`: the prefix test, case-insensitively. */
export function isLabelKeyword(flag: string): boolean {
  const lower = flag.toLowerCase();
  return lower.startsWith('$label_') || lower.startsWith('$label=');
}

/** ASCII case folding — not Unicode: a keyword is an IMAP atom. */
export function sameKeyword(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  return asciiLower(a) === asciiLower(b);
}

function asciiLower(value: string): string {
  return value.replace(/[A-Z]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 32));
}

/** Whether `keyword` names `label` (its keyword or, while it lasts, its old one). */
export function labelHasKeyword(label: Label, keyword: string): boolean {
  return (
    sameKeyword(label.keyword, keyword) ||
    (label.previousKeyword !== null && sameKeyword(label.previousKeyword, keyword))
  );
}

/** The label keywords among raw IMAP flags (an SSE `flags` frame's atoms). */
export function labelKeywordsOf(atoms: readonly string[]): string[] {
  return atoms.filter(isLabelKeyword);
}

/**
 * The labels these keywords name, in the account's label order, each once.
 * Labels being deleted and keywords that name nothing are left out.
 */
export function labelsForKeywords(
  keywords: readonly string[] | undefined,
  labels: readonly Label[] | undefined,
): Label[] {
  if (!keywords || keywords.length === 0 || !labels || labels.length === 0) return [];
  return labels.filter(
    (label) => label.state !== 'deleting' && keywords.some((keyword) => labelHasKeyword(label, keyword)),
  );
}

/** Whether a message carries this label. */
export function messageHasLabel(message: Pick<MessageSummary, 'labelKeywords'>, label: Label): boolean {
  return (message.labelKeywords ?? []).some((keyword) => labelHasKeyword(label, keyword));
}

/** Keywords with `label`'s own (and old) keyword taken out. */
export function withoutLabel(keywords: readonly string[], label: Label): string[] {
  return keywords.filter((keyword) => !labelHasKeyword(label, keyword));
}

/** Keywords with `label` added, unless it is already there in any spelling. */
export function withLabel(keywords: readonly string[], label: Label): string[] {
  return keywords.some((keyword) => labelHasKeyword(label, keyword)) ? [...keywords] : [...keywords, label.keyword];
}

/** Labels the UI can offer: every label that is not being deleted. */
export function liveLabels(labels: readonly Label[] | undefined): Label[] {
  return (labels ?? []).filter((label) => label.state !== 'deleting');
}

/**
 * The query the label view sends (plan/17 §4): `label:"<name>"`. Always
 * quoted, so a name with a space — or one that looks like another operator —
 * is one term. Names can hold neither `"` nor `\` (§2), so nothing needs
 * escaping.
 */
export function labelQuery(label: Pick<Label, 'name'>): string {
  return `label:"${label.name}"`;
}
